import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { devLog, devError } from '../../utils/devLogger';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { AppEmailTextField } from '../../customComponents/AppEmailTextField';
import { AppPwdTextField, PwdFieldType } from '../../customComponents/AppPwdTextField';
import PrimaryButton from '../../components/PrimaryButton';
import VerticalDivider from '../../components/VerticalDivider';
import HorizontalDivider from '../../components/HorizontalDivider';
import CenteredLayout from '../../layout/CenteredLayout';
import LanguageSelector from '../../customComponents/LanguageSelector';
import Appbar from '../../components/Appbar';
import ResponsiveView from '../../layout/ResponsiveView';
import { userLogin } from '../../lib/api/user/userApi';
import { useUserAuth } from '../../context/UserAuthContext';
import { useLang } from '../../context/LangContext';
import { initKakaoSDK, kakaoLogin } from '../../utils/kakaoAuth';
import { initGoogleSDK, googleLogin } from '../../utils/googleAuth';
import { initNaverSDK, naverLogin, getNaverUserInfo } from '../../utils/naverAuth';
import { extractAppleUserData } from '../../utils/appleAuth';
import { socialLogin } from '../../lib/api/user/userApi';
import AppleLoginButton from '../../components/AppleLoginButton';
import useAppleCallback from '../../hooks/useAppleCallback';
import { isShoppingMode } from '../../config/appMode';

const LoginLogo = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
  margin-top: 40px; /* 앱바와의 간격 확보 */
`;

// 데스크톱용 레이아웃 컴포넌트들
const DesktopLoginLayout = styled.div`
  display: flex;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh;
  
  @media (max-width: 767px) {
    min-height: calc(100vh - 84px); /* 모바일에서는 앱바 높이 고려 */
  }
`;

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
`;

const BackgroundImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;

const LeftContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  padding: 40px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BrandLogo = styled.img`
  width: 315px;
  height: 98px;
  object-fit: contain;
  margin-bottom: 24px;
`;

const BrandTitle = styled.h2`
  font-size: 14px;
  font-weight: 500;
  color: #000;
  margin: 0;
  text-shadow: none;
`;

const RightSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
  min-height: 100vh;
  padding-left: 16px;
  padding-right: 16px;
  
  @media (max-width: 767px) {
    min-height: calc(100vh - 84px); /* 모바일에서는 앱바 높이 고려 */
  }
`;

const LoginFormContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 40px 0;
`;

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100vh;
  
  @media (max-width: 767px) {
    min-height: calc(100vh - 84px); /* 모바일에서는 앱바 높이 고려 */
  }
`;

const LogoImage = styled.img`
  width: 224px;
  height: 35px;
  object-fit: contain;
`;

const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
`;

const LoginButtonContainer = styled.div`
  width: 100%;
`;

const ActionLinksContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  gap: 16px;

  @media (max-width: 480px) {
    margin-top: 20px;
    gap: 12px;
  }
`;

const ActionLink = styled.button`
  background: none;
  border: none;
  color: #262626;
  font-size: 15px;
  cursor: pointer;
  padding: 8px 0;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #45423b;
  }

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const SnsLoginContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const SnsLoginButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  transition: transform 0.2s ease;

  &:hover:not(:disabled) {
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    width: 44px;
    height: 44px;
  }
`;

const SnsIcon = styled.img`
  width: 50px;
  height: 50px;
  object-fit: contain;

  @media (max-width: 480px) {
    width: 44px;
    height: 44px;
  }
`;

// 다국어 텍스트 객체
const texts = {
  ko: {
    login: '로그인',
    loggingIn: '로그인 중...',
    findEmail: '이메일 찾기',
    findPassword: '비밀번호 찾기',
    signUp: '회원가입',
    snsLogin: 'SNS 계정으로 로그인',
    loginSuccess: '로그인되었습니다.',
    loginFailed: '이메일 또는 비밀번호가 올바르지 않습니다.',
    loginError: '로그인에 실패했습니다. 다시 시도해주세요.',
    kakaoLoginSuccess: '카카오 로그인 성공!',
    kakaoLoginError: '카카오 로그인에 실패했습니다. 다시 시도해주세요.',
    googleLoginSuccess: '구글 로그인 성공!',
    googleLoginError: '구글 로그인에 실패했습니다. 다시 시도해주세요.',
    naverLoginSuccess: '네이버 로그인 성공!',
    naverLoginError: '네이버 로그인에 실패했습니다. 다시 시도해주세요.',
    appleLoginReady: '애플 로그인은 준비 중입니다.',
    serverLoginError: '서버 로그인에 실패했습니다. 다시 시도해주세요.',
    brandTitle: '경매의 신뢰, 예술의 가치',
    additionalInfoRequired: '추가 정보 입력이 필요합니다.',
  },
  en: {
    login: 'Login',
    loggingIn: 'Logging in...',
    findEmail: 'Find Email',
    findPassword: 'Find Password',
    signUp: 'Sign Up',
    snsLogin: 'Login with SNS Account',
    loginSuccess: 'Successfully logged in.',
    loginFailed: 'Incorrect email or password.',
    loginError: 'Login failed. Please try again.',
    kakaoLoginSuccess: 'Kakao login successful!',
    kakaoLoginError: 'Kakao login failed. Please try again.',
    googleLoginSuccess: 'Google login successful!',
    googleLoginError: 'Google login failed. Please try again.',
    naverLoginSuccess: 'Naver login successful!',
    naverLoginError: 'Naver login failed. Please try again.',
    appleLoginReady: 'Apple login is under preparation.',
    serverLoginError: 'Server login failed. Please try again.',
    brandTitle: 'Trust in Auctions, Value in Art',
    additionalInfoRequired: 'Additional information input is required.',
  },
};

function Login() {
  // Apple 로그인 콜백 처리
  useAppleCallback();
  
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoggedIn } = useUserAuth();
  const { lang } = useLang();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const t = texts[lang];

  // 이메일 찾기에서 전달받은 이메일 값 자동 설정
  useEffect(() => {
    const stateEmail = location.state?.email;
    if (stateEmail) {
      setEmail(stateEmail);
      setIsEmailValid(true); // 이메일 찾기에서 온 값이므로 유효함
    }
  }, [location.state]);

  // 네이버 로그인 리다이렉트 처리
  const handleNaverLoginCallback = useCallback(async () => {
    try {
      const naverLoginInProgress = sessionStorage.getItem('naverLoginInProgress');
      
      if (naverLoginInProgress === 'true') {
        devLog('=== 네이버 로그인 리다이렉트 처리 시작 ===');
        
        // 네이버 로그인 상태 확인
        if (window.naverLogin) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // 1초 대기
          
          window.naverLogin.getLoginStatus(async (status: boolean) => {
            if (status) {
              devLog('=== 네이버 리다이렉트 후 로그인 상태 확인 성공 ===');
              
              try {
                const naverResponse = await getNaverUserInfo();
                devLog('=== 네이버 로그인 SDK 응답 ===', naverResponse);
                
                // 서버에 소셜 로그인 요청
                devLog('=== 서버 소셜 로그인 API 호출 시작 ===');
                const socialLoginParams = {
                  providerId: naverResponse.providerId,
                  accessToken: naverResponse.accessToken,
                  email: naverResponse.userInfo.email || `naver_${naverResponse.providerId}`,
                  name: naverResponse.userInfo.name || naverResponse.userInfo.nickname || '네이버 사용자',
                };
                devLog('소셜 로그인 파라미터:', socialLoginParams);
                
                const socialLoginResponse = await socialLogin('naver', socialLoginParams);
                devLog('=== 네이버 소셜 로그인 서버 응답 ===', socialLoginResponse);

                if (socialLoginResponse && socialLoginResponse.data && socialLoginResponse.data.statusCode === 200) {
                  devLog('=== 서버 로그인 성공 - 사용자 데이터 구성 ===');
                  
                  // 일반 로그인과 동일한 방식으로 토큰 추출 (헤더 우선, 데이터 fallback)
                  const accessToken = socialLoginResponse.headers?.get('access_token') || 
                                     socialLoginResponse.headers?.get('access-token') || 
                                     socialLoginResponse.headers?.get('authorization')?.replace('Bearer ', '') ||
                                     socialLoginResponse.data.data?.accessToken ||
                                     socialLoginResponse.data.data?.token || '';

                  devLog('=== 저장할 서버 응답 데이터 ===', socialLoginResponse.data.data);
                  devLog('=== 저장할 토큰 ===', accessToken);
                  devLog('🔍 서버 응답에서 리프레시 토큰 확인:', socialLoginResponse.data.data?.refreshToken);
                  devLog('🔍 needMoreInfo 값 확인:', socialLoginResponse.data.data?.needMoreInfo);

                  // Context에 서버 응답 전체 데이터 전달 (additionalInfo 계산을 위해)
                  devLog('=== Context에 로그인 정보 저장 시작 ===');
                  login(socialLoginResponse.data.data, accessToken);
                  devLog('=== Context에 로그인 정보 저장 완료 ===');
                  
                  toast.success(t.naverLoginSuccess);
                  
                  // 세션 스토리지 정리
                  sessionStorage.removeItem('naverLoginInProgress');
                  sessionStorage.removeItem('naverLoginResolve');
                  
                  // needMoreInfo가 true인 경우 추가 정보 입력 페이지로, 아니면 홈으로 이동
                  if (socialLoginResponse.data.data?.needMoreInfo) {
                    devLog('=== 추가 정보 입력 페이지로 이동 ===');
                    navigate('/additional-info');
                  } else {
                    devLog('=== 홈으로 이동 시도 ===');
                    navigate('/');
                  }
                } else {
                  devError('=== 서버 로그인 실패 ===', socialLoginResponse);
                  toast.error(t.serverLoginError);
                  sessionStorage.removeItem('naverLoginInProgress');
                  sessionStorage.removeItem('naverLoginResolve');
                }
                
              } catch (error: any) {
                devError('=== 네이버 로그인 리다이렉트 처리 실패 ===', error);
                
                // 에러 메시지 파싱
                let errorMessage = t.naverLoginError;
                
                if (error?.data?.error?.customMessage) {
                  errorMessage = error.data.error.customMessage;
                } else if (error?.data?.message) {
                  errorMessage = error.data.message;
                } else if (error?.message) {
                  errorMessage = error.message;
                }
                
                toast.error(errorMessage);
                sessionStorage.removeItem('naverLoginInProgress');
                sessionStorage.removeItem('naverLoginResolve');
              }
            } else {
              devLog('=== 네이버 리다이렉트 후 로그인 상태 아님 ===');
              sessionStorage.removeItem('naverLoginInProgress');
              sessionStorage.removeItem('naverLoginResolve');
            }
          });
        }
      }
    } catch (error) {
      devError('네이버 로그인 리다이렉트 처리 중 오류:', error);
      sessionStorage.removeItem('naverLoginInProgress');
      sessionStorage.removeItem('naverLoginResolve');
    }
  }, [login, navigate, t]);

  // 이미 로그인된 상태라면 홈으로 리다이렉트
  useEffect(() => {
    devLog('로그인 상태 확인:', { isLoggedIn });
    if (isLoggedIn) {
      devLog('이미 로그인된 상태 - 홈으로 리다이렉트');
      navigate('/');
    }
  }, [isLoggedIn, navigate]);

  // 카카오, 네이버, 구글 SDK 초기화 및 네이버 리다이렉트 처리
  useEffect(() => {
    const initSDKs = async () => {
      try {
        // DOM이 완전히 로드될 때까지 대기
        if (document.readyState !== 'complete') {
          await new Promise((resolve) => {
            window.addEventListener('load', resolve, { once: true });
          });
        }

        // 추가 지연으로 스크립트 완전 로딩 보장
        await new Promise(resolve => setTimeout(resolve, 500));

        // 카카오 SDK 초기화
        let kakaoInitialized = false;
        for (let i = 0; i < 5; i++) {
          kakaoInitialized = initKakaoSDK();
          if (kakaoInitialized) break;
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        // 네이버 SDK 초기화
        let naverInitialized = false;
        for (let i = 0; i < 5; i++) {
          naverInitialized = initNaverSDK();
          if (naverInitialized) break;
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        // 구글 SDK 초기화
        let googleInitialized = false;
        for (let i = 0; i < 5; i++) {
          googleInitialized = initGoogleSDK();
          if (googleInitialized) break;
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        // 애플 SDK 초기화 (애플 개발자 계정 유료 가입 후 활성화)
        // let appleInitialized = false;
        // for (let i = 0; i < 5; i++) {
        //   appleInitialized = initAppleSDK();
        //   if (appleInitialized) break;
        //   await new Promise(resolve => setTimeout(resolve, 200));
        // }

        devLog('SDK 초기화 결과:', { 
          kakao: kakaoInitialized, 
          naver: naverInitialized,
          google: googleInitialized,
          // apple: appleInitialized // 애플 개발자 계정 유료 가입 후 활성화
        });

        // 네이버 로그인 리다이렉트 처리
        if (naverInitialized) {
          await handleNaverLoginCallback();
        }

      } catch (error) {
        devError('SDK 초기화 중 오류:', error);
      }
    };

    initSDKs();
  }, [handleNaverLoginCallback]);

  // Apple POST 요청 처리 (Form 데이터로 전송된 경우)
  useEffect(() => {
    const handleApplePostCallback = async () => {
      try {
        // Apple에서 리다이렉트로 온 것인지 확인
        // Apple은 POST로 Form 데이터를 보내지만, 브라우저에서는 URL 파라미터로 확인 가능
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');  
        const id_token = urlParams.get('id_token');

        // Apple 로그인 파라미터가 있을 때만 처리
        if (!code || !state) {
          return; // Apple 로그인이 아니면 early return
        }

        console.log('=== Apple 콜백 확인 시작 ===');
        console.log('Current URL:', window.location.href);
        console.log('Search params:', window.location.search);
        console.log('Hash:', window.location.hash);
        console.log('Extracted params:', { code, state, id_token: id_token ? 'Present' : 'Missing' });

        if (code && state === 'bidspace_apple_login') {
          console.log('=== Valid Apple 콜백 데이터 확인됨 ===');

          setIsLoading(true);

          // Apple 사용자 데이터 추출 (URL 파라미터에서) - 리다이렉트 방식에서는 user 정보가 URL에 포함될 수 있음
          console.log('=== Apple 리다이렉트에서 사용자 데이터 추출 시도 ===');
          const appleUserData = extractAppleUserData({
            authorization: {
              code: code,
              id_token: id_token || '',
              state: state
            }
          });
          
          console.log('=== 추출된 Apple 사용자 데이터 ===');
          console.log('Email:', appleUserData.email);
          console.log('Name:', appleUserData.name);
          console.log('HasRealName:', appleUserData.hasRealName);
          console.log('Provider ID:', appleUserData.providerId);

          // socialLogin API 호출
          const socialLoginParams = {
            providerId: appleUserData.providerId,
            accessToken: id_token || '',
            email: appleUserData.email, // 추출된 실제 이메일 사용
            ...(appleUserData.name && { name: appleUserData.name }), // 이름이 있을 때만 포함
          };

          console.log('Apple 소셜 로그인 시도 중...', socialLoginParams);
          const socialLoginResponse = await socialLogin('apple', socialLoginParams);
          console.log('Apple 소셜 로그인 응답:', socialLoginResponse);

          if (socialLoginResponse && socialLoginResponse.data && socialLoginResponse.data.statusCode === 200) {
            // 일반 로그인과 동일한 방식으로 토큰 추출 (헤더 우선, 데이터 fallback)
            const accessToken = socialLoginResponse.headers?.get('access_token') || 
                               socialLoginResponse.headers?.get('access-token') || 
                               socialLoginResponse.headers?.get('authorization')?.replace('Bearer ', '') ||
                               socialLoginResponse.data.data?.accessToken ||
                               socialLoginResponse.data.data?.token || '';
            
            // 서버에서 토큰을 제대로 반환하지 않으면 에러 처리
            if (!accessToken) {
              throw new Error('서버에서 유효한 토큰을 반환하지 않았습니다.');
            }
            
            login(socialLoginResponse.data.data, accessToken);
            const successMessage = lang === 'ko' ? 'Apple 로그인 성공!' : 'Apple login successful!';
            toast.success(successMessage);
            
            // URL 파라미터 제거
            window.history.replaceState({}, document.title, window.location.pathname);
            
            setTimeout(() => {
              if (socialLoginResponse.data.data?.needMoreInfo) {
                navigate('/additional-info');
              } else {
                navigate('/');
              }
            }, 100);
          } else {
            throw new Error('Apple 로그인 서버 응답 실패');
          }
        } else if (code) {
          console.log('Apple 콜백이지만 state가 일치하지 않음:', state);
        } else {
          console.log('Apple 콜백 파라미터 없음');
        }
      } catch (error) {
        console.error('Apple 콜백 처리 오류:', error);
        const errorMessage = lang === 'ko' ? 'Apple 로그인에 실패했습니다. 다시 시도해주세요.' : 'Apple login failed. Please try again.';
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    // 컴포넌트 마운트 시와 URL 변경 시 실행
    handleApplePostCallback();
  }, [location.search, login, navigate, lang]);

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEmail(e.target.value);
  }, []);

  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPassword(e.target.value);
  }, []);

  const handleLogin = useCallback(async () => {
    if (isLoading) return; // 이미 로딩 중이면 중복 실행 방지

    setIsLoading(true);

    try {
      devLog('로그인 시도:', { email, password });
      
      const response = await userLogin({
        email: email.trim(),
        password: password.trim(),
      });

      devLog('로그인 성공:', response);
      
      // 로그인 성공 처리
      if (response && response.data && response.data.statusCode === 200) {
        toast.success(t.loginSuccess);
        
        // 응답 헤더에서 토큰 추출 (일반 로그인은 헤더에 토큰을 설정함)
        const accessToken = response.headers?.get('access_token') || 
                           response.headers?.get('access-token') || 
                           response.headers?.get('authorization')?.replace('Bearer ', '') ||
                           response.data.data?.token || '';
        
        // 헤더에서 리프레시 토큰도 추출
        const refreshToken = response.headers?.get('refresh_token') || 
                           response.headers?.get('refresh-token') || 
                           response.data.data?.refreshToken || '';
        
        devLog('추출된 액세스 토큰:', accessToken);
        devLog('추출된 리프레시 토큰:', refreshToken);
        devLog('응답 헤더:', response.headers);
        devLog('🔍 needMoreInfo 값 확인:', response.data.data?.needMoreInfo);
        
        // 서버 응답 데이터에 리프레시 토큰 추가 (Flutter 전송용)
        const serverDataWithRefreshToken = {
          ...response.data.data,
          refreshToken: refreshToken
        };
        
        // Context에 서버 응답 전체 데이터 전달 (additionalInfo 계산을 위해)
        login(serverDataWithRefreshToken, accessToken);
        
        // needMoreInfo가 true인 경우 추가 정보 입력 페이지로, 아니면 홈으로 이동
        if (response.data.data?.needMoreInfo) {
          devLog('=== 추가 정보 입력 페이지로 이동 ===');
          navigate('/additional-info');
        } else {
          devLog('=== 홈으로 이동 시도 ===');
          navigate('/');
        }
      } else {
        toast.error(t.loginFailed);
      }
    } catch (error: any) {
      devError('로그인 실패:', error);
      
      // 에러 메시지 파싱 - 커스텀 메시지 우선
      let errorMessage = t.loginError; // 기본 메시지를 일반적인 오류 메시지로 변경
      
      // 서버에서 보내는 커스텀 메시지가 있으면 최우선으로 사용
      if (error?.response?.data?.error?.customMessage) {
        errorMessage = error.response.data.error.customMessage;
      } else if (error?.data?.error?.customMessage) {
        errorMessage = error.data.error.customMessage;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.status === 401) {
        // 401 상태코드일 때는 인증 실패 메시지
        errorMessage = t.loginFailed;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, email, password, t, login, navigate]);

  const handleFindEmail = useCallback(() => {
    devLog('이메일 찾기');
    navigate('/find-email');
  }, [navigate]);

  const handleFindPassword = useCallback(() => {
    devLog('비밀번호 찾기');
    navigate('/find-password');
  }, [navigate]);

  const handleSignUp = useCallback(() => {
    navigate('/signup');
  }, [navigate]);

  const handleBackToHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleKakaoLogin = useCallback(async () => {
    try {
      devLog('카카오 로그인 시작');
      setIsLoading(true);
      
      const kakaoResponse = await kakaoLogin();
      devLog('카카오 로그인 성공:', kakaoResponse);
      
      // 카카오에서 받은 정보로 서버 API 호출
      devLog('=== 서버 소셜 로그인 API 호출 시작 ===');
      const socialLoginParams = {
        providerId: kakaoResponse.providerId,
        accessToken: kakaoResponse.accessToken,
        email: kakaoResponse.userInfo.kakao_account?.email || ``,
        name: kakaoResponse.userInfo.kakao_account?.profile?.nickname || '',
      };
      devLog('카카오 소셜 로그인 파라미터:', socialLoginParams);
      
      const socialLoginResponse = await socialLogin('kakao', socialLoginParams);
      devLog('=== 카카오 소셜 로그인 서버 응답 ===', socialLoginResponse);

      if (socialLoginResponse && socialLoginResponse.data && socialLoginResponse.data.statusCode === 200) {
        devLog('=== 서버 로그인 성공 - 사용자 데이터 구성 ===');
        
        // 일반 로그인과 동일한 방식으로 토큰 추출 (헤더 우선, 데이터 fallback)
        const accessToken = socialLoginResponse.headers?.get('access_token') || 
                           socialLoginResponse.headers?.get('access-token') || 
                           socialLoginResponse.headers?.get('authorization')?.replace('Bearer ', '') ||
                           socialLoginResponse.data.data?.accessToken ||
                           socialLoginResponse.data.data?.token || '';

        // 서버에서 토큰을 제대로 반환하지 않으면 에러 처리
        if (!accessToken) {
          throw new Error('서버에서 유효한 토큰을 반환하지 않았습니다.');
        }

        devLog('=== 저장할 서버 응답 데이터 ===', socialLoginResponse.data.data);
        devLog('=== 저장할 토큰 ===', accessToken);
        devLog('🔍 서버 응답에서 리프레시 토큰 확인:', socialLoginResponse.data.data?.refreshToken);
        devLog('🔍 needMoreInfo 값 확인:', socialLoginResponse.data.data?.needMoreInfo);

        // Context에 서버 응답 전체 데이터 전달 (additionalInfo 계산을 위해)
        devLog('=== Context에 로그인 정보 저장 시작 ===');
        login(socialLoginResponse.data.data, accessToken);
        devLog('=== Context에 로그인 정보 저장 완료 ===');
        
        toast.success(t.kakaoLoginSuccess);
        
        // needMoreInfo가 true인 경우 추가 정보 입력 페이지로, 아니면 홈으로 이동
        setTimeout(() => {
          if (socialLoginResponse.data.data?.needMoreInfo) {
            devLog('=== 추가 정보 입력 페이지로 이동 ===');
            navigate('/additional-info');
          } else {
            devLog('=== 홈으로 이동 시도 ===');
            navigate('/');
          }
        }, 100);
      } else {
        devError('=== 서버 로그인 실패 ===', socialLoginResponse);
        toast.error(t.serverLoginError);
      }
      
    } catch (error: any) {
      devError('카카오 로그인 실패:', error);
      
      // 에러 메시지 파싱
      let errorMessage = t.kakaoLoginError;
      
      if (error?.data?.error?.customMessage) {
        errorMessage = error.data.error.customMessage;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [login, navigate, t]);

  const handleGoogleLogin = useCallback(async () => {
    try {
      devLog('구글 로그인 시작');
      setIsLoading(true);
      
      const googleResponse = await googleLogin();
      devLog('구글 로그인 성공:', googleResponse);
      
      // 구글에서 받은 정보로 서버 API 호출
      devLog('=== 서버 소셜 로그인 API 호출 시작 ===');
      const socialLoginParams = {
        providerId: googleResponse.providerId,
        accessToken: googleResponse.accessToken,
        email: googleResponse.userInfo.email || `google_${googleResponse.providerId}`,
        name: googleResponse.userInfo.name || googleResponse.userInfo.given_name || '구글 사용자',
      };
      devLog('구글 소셜 로그인 파라미터:', socialLoginParams);
      
      const socialLoginResponse = await socialLogin('google', socialLoginParams);
      devLog('=== 구글 소셜 로그인 서버 응답 ===', socialLoginResponse);

      if (socialLoginResponse && socialLoginResponse.data && socialLoginResponse.data.statusCode === 200) {
        devLog('=== 서버 로그인 성공 - 사용자 데이터 구성 ===');
        
        // 일반 로그인과 동일한 방식으로 토큰 추출 (헤더 우선, 데이터 fallback)
        const accessToken = socialLoginResponse.headers?.get('access_token') || 
                           socialLoginResponse.headers?.get('access-token') || 
                           socialLoginResponse.headers?.get('authorization')?.replace('Bearer ', '') ||
                           socialLoginResponse.data.data?.accessToken ||
                           socialLoginResponse.data.data?.token || '';

        // 서버에서 토큰을 제대로 반환하지 않으면 에러 처리
        if (!accessToken) {
          throw new Error('서버에서 유효한 토큰을 반환하지 않았습니다.');
        }

        devLog('=== 저장할 서버 응답 데이터 ===', socialLoginResponse.data.data);
        devLog('=== 저장할 토큰 ===', accessToken);
        devLog('🔍 needMoreInfo 값 확인:', socialLoginResponse.data.data?.needMoreInfo);

        // Context에 서버 응답 전체 데이터 전달 (additionalInfo 계산을 위해)
        devLog('=== Context에 로그인 정보 저장 시작 ===');
        login(socialLoginResponse.data.data, accessToken);
        devLog('=== Context에 로그인 정보 저장 완료 ===');
        
        toast.success(t.googleLoginSuccess);
        
        // needMoreInfo가 true인 경우 추가 정보 입력 페이지로, 아니면 홈으로 이동
        setTimeout(() => {
          if (socialLoginResponse.data.data?.needMoreInfo) {
            devLog('=== 추가 정보 입력 페이지로 이동 ===');
            navigate('/additional-info');
          } else {
            devLog('=== 홈으로 이동 시도 ===');
            navigate('/');
          }
        }, 100);
      } else {
        devError('=== 서버 로그인 실패 ===', socialLoginResponse);
        toast.error(t.serverLoginError);
      }
      
    } catch (error: any) {
      devError('구글 로그인 실패:', error);
      
      // 에러 메시지 파싱
      let errorMessage = t.googleLoginError;
      
      if (error?.data?.error?.customMessage) {
        errorMessage = error.data.error.customMessage;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [login, navigate, t]);

  const handleNaverLogin = useCallback(async () => {
    try {
      devLog('=== 네이버 로그인 시작 ===');
      setIsLoading(true);
      
      devLog('네이버 SDK 초기화 상태 확인:', !!window.naverLogin);
      
      const naverResponse = await naverLogin();
      devLog('=== 네이버 로그인 SDK 응답 ===', naverResponse);
      
      // 네이버에서 받은 정보로 서버 API 호출 또는 직접 로그인 처리
      if (naverResponse && naverResponse.userInfo && naverResponse.providerId) {
        // 서버에 소셜 로그인 요청
        devLog('=== 서버 소셜 로그인 API 호출 시작 ===');
        const socialLoginParams = {
          providerId: naverResponse.providerId,
          accessToken: naverResponse.accessToken,
          email: naverResponse.userInfo.email || `naver_${naverResponse.providerId}`,
          name: naverResponse.userInfo.name || naverResponse.userInfo.nickname || '',
        };
        devLog('소셜 로그인 파라미터:', socialLoginParams);
        
        const socialLoginResponse = await socialLogin('naver', socialLoginParams);
        devLog('=== 네이버 소셜 로그인 서버 응답 ===', socialLoginResponse);

        if (socialLoginResponse && socialLoginResponse.data && socialLoginResponse.data.statusCode === 200) {
          devLog('=== 서버 로그인 성공 - 사용자 데이터 구성 ===');
          
          // 일반 로그인과 동일한 방식으로 토큰 추출 (헤더 우선, 데이터 fallback)
          const accessToken = socialLoginResponse.headers?.get('access_token') || 
                             socialLoginResponse.headers?.get('access-token') || 
                             socialLoginResponse.headers?.get('authorization')?.replace('Bearer ', '') ||
                             socialLoginResponse.data.data?.accessToken ||
                             socialLoginResponse.data.data?.token || '';

          // 서버에서 토큰을 제대로 반환하지 않으면 에러 처리
          if (!accessToken) {
            throw new Error('서버에서 유효한 토큰을 반환하지 않았습니다.');
          }

          devLog('=== 저장할 서버 응답 데이터 ===', socialLoginResponse.data.data);
          devLog('=== 저장할 토큰 ===', accessToken);
          devLog('🔍 needMoreInfo 값 확인:', socialLoginResponse.data.data?.needMoreInfo);

          // Context에 서버 응답 전체 데이터 전달 (additionalInfo 계산을 위해)
          devLog('=== Context에 로그인 정보 저장 시작 ===');
          login(socialLoginResponse.data.data, accessToken);
          devLog('=== Context에 로그인 정보 저장 완료 ===');
          
          toast.success(t.naverLoginSuccess);
          
          // needMoreInfo가 true인 경우 추가 정보 입력 페이지로, 아니면 홈으로 이동
          setTimeout(() => {
            if (socialLoginResponse.data.data?.needMoreInfo) {
              devLog('=== 추가 정보 입력 페이지로 이동 ===');
              navigate('/additional-info');
            } else {
              devLog('=== 홈으로 이동 시도 ===');
              navigate('/');
            }
          }, 100);
        } else {
          devError('=== 서버 로그인 실패 ===', socialLoginResponse);
          toast.error(t.serverLoginError);
        }
      } else {
        // 서버 API 없이 직접 로그인 처리 (카카오 방식과 동일)
        devLog('=== 직접 네이버 로그인 처리 ===');
        const userData = {
          userId: naverResponse.providerId,
          email: naverResponse.userInfo.email || `naver_${naverResponse.providerId}`,
          name: naverResponse.userInfo.name || naverResponse.userInfo.nickname || '네이버 사용자',
          provider: 'naver',
          providerId: naverResponse.providerId,
        };

        // Context에 로그인 정보 저장
        login(userData, naverResponse.accessToken);
        
        toast.success(t.naverLoginSuccess);
        navigate('/');
      }
      
    } catch (error: any) {
      devError('=== 네이버 로그인 실패 ===', error);
      
      // 에러 메시지 파싱
      let errorMessage = t.naverLoginError;
      
      if (error?.data?.error?.customMessage) {
        errorMessage = error.data.error.customMessage;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      devError('=== 최종 에러 메시지 ===', errorMessage);
      toast.error(errorMessage);
    } finally {
      devLog('=== 네이버 로그인 프로세스 종료 ===');
      setIsLoading(false);
    }
  }, [login, navigate, t]);



  const isFormValid = useMemo(() => 
    isEmailValid && isPasswordValid && email.trim() !== '' && password.trim() !== '', 
    [isEmailValid, isPasswordValid, email, password]
  );

  // 엔터 키 핸들러 추가
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isFormValid && !isLoading) {
      e.preventDefault();
      handleLogin();
    }
  }, [isFormValid, isLoading, handleLogin]);

  // 로그인 폼 컴포넌트 (공통) - useMemo로 메모이제이션
  const LoginForm = useMemo(() => (
    <div key="login-form">
      <LoginLogo>
        <LogoImage src="/logo.png" alt="Logo" />
      </LoginLogo>

      <FormFields>
        <AppEmailTextField
          key="email-field"
          value={email}
          onChange={handleEmailChange}
          onValidationChange={setIsEmailValid}
          language={lang}
          autoFocus={false}
          showValidationMessage={true}
        />
        
        <AppPwdTextField
          key="password-field"
          value={password}
          onChange={handlePasswordChange}
          onKeyDown={handleKeyDown} // 추가
          onValidationChange={setIsPasswordValid}
          fieldType={PwdFieldType.PASSWORD}
          language={lang}
          autoComplete="current-password"
          showValidationMessage={true}
        />
      </FormFields>

      <LoginButtonContainer>
        <PrimaryButton
          onClick={handleLogin}
          disabled={!isFormValid || isLoading}
        >
          {isLoading ? t.loggingIn : t.login}
        </PrimaryButton>
      </LoginButtonContainer>

      <ActionLinksContainer>
        <ActionLink onClick={handleFindEmail}>
          {t.findEmail}
        </ActionLink>
        <VerticalDivider height={20} color="#cccccc" />
        <ActionLink onClick={handleFindPassword}>
          {t.findPassword}
        </ActionLink>
        <VerticalDivider height={20} color="#cccccc" />
        <ActionLink onClick={handleSignUp}>
          {t.signUp}
        </ActionLink>
      </ActionLinksContainer>

      <HorizontalDivider text={t.snsLogin} />

      <SnsLoginContainer>
        <SnsLoginButton onClick={handleKakaoLogin} disabled={isLoading}>
          <SnsIcon src="/login/kakao.svg" alt="카카오" />
        </SnsLoginButton>
        <SnsLoginButton onClick={handleGoogleLogin} disabled={isLoading}>
          <SnsIcon src="/login/google.svg" alt="구글" />
        </SnsLoginButton>
        <SnsLoginButton onClick={handleNaverLogin} disabled={isLoading}>
          <SnsIcon src="/login/naver.svg" alt="네이버" />
        </SnsLoginButton>
        <AppleLoginButton 
          disabled={isLoading}
          onStartLoading={() => setIsLoading(true)}
          onStopLoading={() => setIsLoading(false)}
        />
      </SnsLoginContainer>
    </div>
  ), [
    email, 
    password, 
    handleEmailChange, 
    handlePasswordChange, 
    handleKeyDown, // 추가
    lang, 
    handleLogin, 
    isFormValid, 
    isLoading, 
    t.loggingIn,
    t.login,
    t.findEmail,
    t.findPassword,
    t.signUp,
    t.snsLogin,
    handleFindEmail, 
    handleFindPassword, 
    handleSignUp, 
    handleKakaoLogin, 
    handleGoogleLogin, 
    handleNaverLogin
  ]);

  // 모바일 레이아웃 - useMemo로 메모이제이션
  const MobileLayout = useMemo(() => (
    <CenteredLayout>
      <Appbar
        right={<LanguageSelector />}
        showBackButton={true}
        onBack={handleBackToHome}
        $transparent={true}
        $marginBottom="0"
      />
      
      <LoginContainer>
        {LoginForm}
      </LoginContainer>
    </CenteredLayout>
  ), [LoginForm, handleBackToHome]);

  // 데스크톱 레이아웃 - useMemo로 메모이제이션
  const DesktopLayout = useMemo(() => (
    <div style={{ width: '100%', minHeight: '100vh' }}>
      <DesktopLoginLayout>
        <LeftSection>
          <BackgroundImage src="/login_bg.svg" alt="Login Background" />
          <LeftContent>
            <BrandLogo src="/logo.png" alt="BidSpace Logo" />
            {!isShoppingMode() && <BrandTitle>{t.brandTitle}</BrandTitle>}
          </LeftContent>
        </LeftSection>
        
        <RightSection>
          <Appbar
            right={<LanguageSelector />}
            $transparent={true}
            $marginBottom="0"
          />
          <LoginFormContainer>
            {LoginForm}
          </LoginFormContainer>
        </RightSection>
      </DesktopLoginLayout>
    </div>
  ), [LoginForm, t.brandTitle]);

  return (
    <ResponsiveView
      mobileView={MobileLayout}
      desktopView={DesktopLayout}
    />
  );
}

export default Login;
